import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { format, addDays, differenceInCalendarDays } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import {
  blockCoversDriver,
  blocksStoppingRequest,
  formatBlockDates,
  loadBlockDriver,
  loadTimeOffBlocks,
  type BlockDriver,
  type TimeOffBlock,
} from "@/lib/timeOffBlocks";
import {
  Ban,
  Clock,
  Calendar,
  CalendarRange,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

export type TimeOffRequest = {
  id: string;
  driver_name: string;
  request_type: "partial_day" | "full_day" | "multi_day";
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string;
  status: "pending" | "approved" | "denied";
  admin_notes: string | null;
  requested_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
};

interface TimeOffRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driverName: string;
  onCreated: () => void;
  defaultDate?: string;
}

const TimeOffRequestDialog: React.FC<TimeOffRequestDialogProps> = ({
  open,
  onOpenChange,
  driverName,
  onCreated,
  defaultDate,
}) => {
  const [requestType, setRequestType] = useState<"partial_day" | "full_day" | "multi_day">("partial_day");
  const [startDate, setStartDate] = useState(defaultDate || format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(defaultDate || format(new Date(), "yyyy-MM-dd"));
  const [startTime, setStartTime] = useState("12:00");
  const [endTime, setEndTime] = useState("15:00");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Dates the office has blocked for leave, and which of them cover this driver.
  const [blocks, setBlocks] = useState<TimeOffBlock[]>([]);
  const [blockDriver, setBlockDriver] = useState<BlockDriver>({ groups: [], driverTypeId: null });
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([loadTimeOffBlocks(), loadBlockDriver(driverName)]).then(([b, d]) => {
      if (cancelled) return;
      setBlocks(b);
      setBlockDriver(d);
    });
    return () => {
      cancelled = true;
    };
  }, [open, driverName]);

  const today = format(new Date(), "yyyy-MM-dd");
  const upcomingBlocks = useMemo(
    () => blocks.filter((b) => b.is_active && b.end_date.slice(0, 10) >= today && blockCoversDriver(b, blockDriver)),
    [blocks, blockDriver, today]
  );
  const stoppingBlocks = useMemo(
    () =>
      blocksStoppingRequest(blocks, blockDriver, {
        type: requestType,
        startDate,
        endDate: requestType === "multi_day" ? endDate : startDate,
        startTime: requestType === "partial_day" ? startTime : null,
        endTime: requestType === "partial_day" ? endTime : null,
      }),
    [blocks, blockDriver, requestType, startDate, endDate, startTime, endTime]
  );

  const resetForm = () => {
    setRequestType("partial_day");
    setStartDate(defaultDate || format(new Date(), "yyyy-MM-dd"));
    setEndDate(defaultDate || format(new Date(), "yyyy-MM-dd"));
    setStartTime("12:00");
    setEndTime("15:00");
    setReason("");
  };

  const handleSubmit = async () => {
    if (stoppingBlocks.length > 0) {
      toast({ title: "Leave isn't available for those dates", description: stoppingBlocks.map((b) => b.message).join(" "), variant: "destructive" });
      return;
    }
    if (!reason.trim()) {
      toast({ title: "Required", description: "Please provide a reason for your time off request", variant: "destructive" });
      return;
    }

    if (requestType === "partial_day") {
      if (!startTime || !endTime) {
        toast({ title: "Required", description: "Please specify start and end times", variant: "destructive" });
        return;
      }
      if (startTime >= endTime) {
        toast({ title: "Invalid", description: "End time must be after start time", variant: "destructive" });
        return;
      }
    }

    if (requestType === "multi_day" && endDate < startDate) {
      toast({ title: "Invalid", description: "End date must be on or after start date", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        driver_name: driverName,
        request_type: requestType,
        start_date: startDate,
        end_date: requestType === "multi_day" ? endDate : startDate,
        start_time: requestType === "partial_day" ? startTime : null,
        end_time: requestType === "partial_day" ? endTime : null,
        reason: reason.trim(),
        status: "pending",
        requested_at: new Date().toISOString(),
      };

      const { error } = await supabase.from("roster_time_off_requests").insert(payload);
      if (error) throw error;

      toast({ title: "Request Submitted", description: "Your time off request has been submitted for approval" });
      resetForm();
      onOpenChange(false);
      onCreated();
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to submit request", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const calcHours = () => {
    if (requestType === "partial_day" && startTime && endTime) {
      const [sh, sm] = startTime.split(":").map(Number);
      const [eh, em] = endTime.split(":").map(Number);
      const mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins > 0) {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return m === 0 ? `${h}h` : `${h}h ${m}m`;
      }
    }
    if (requestType === "full_day") return "Full day";
    if (requestType === "multi_day") {
      const days = differenceInCalendarDays(new Date(endDate + "T00:00:00"), new Date(startDate + "T00:00:00")) + 1;
      return `${days} day${days !== 1 ? "s" : ""}`;
    }
    return "";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="max-w-md bg-gray-900 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
              <Calendar className="h-4 w-4 text-orange-400" />
            </div>
            Request Time Off
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Request Type */}
          <div>
            <Label className="text-gray-300 text-xs uppercase tracking-wider">Type</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              <button
                onClick={() => { setRequestType("partial_day"); setEndDate(startDate); }}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-all ${
                  requestType === "partial_day"
                    ? "border-blue-500 bg-blue-950/40 text-blue-300"
                    : "border-gray-700 bg-gray-800/60 text-gray-400 hover:border-gray-600"
                }`}
              >
                <Clock className="h-4 w-4" />
                <span className="text-xs font-medium">Hours</span>
              </button>
              <button
                onClick={() => { setRequestType("full_day"); setEndDate(startDate); }}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-all ${
                  requestType === "full_day"
                    ? "border-blue-500 bg-blue-950/40 text-blue-300"
                    : "border-gray-700 bg-gray-800/60 text-gray-400 hover:border-gray-600"
                }`}
              >
                <Calendar className="h-4 w-4" />
                <span className="text-xs font-medium">Full Day</span>
              </button>
              <button
                onClick={() => setRequestType("multi_day")}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-all ${
                  requestType === "multi_day"
                    ? "border-blue-500 bg-blue-950/40 text-blue-300"
                    : "border-gray-700 bg-gray-800/60 text-gray-400 hover:border-gray-600"
                }`}
              >
                <CalendarRange className="h-4 w-4" />
                <span className="text-xs font-medium">Multi-Day</span>
              </button>
            </div>
          </div>

          {/* Date(s) */}
          <div className={requestType === "multi_day" ? "grid grid-cols-2 gap-3" : ""}>
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">
                {requestType === "multi_day" ? "Start Date" : "Date"}
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (requestType !== "multi_day") setEndDate(e.target.value);
                }}
                className="bg-gray-800 border-gray-600 mt-1"
              />
            </div>
            {requestType === "multi_day" && (
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wider">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className="bg-gray-800 border-gray-600 mt-1"
                />
              </div>
            )}
          </div>

          {/* Time (partial day only) */}
          {requestType === "partial_day" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wider">From</Label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="bg-gray-800 border-gray-600 mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wider">To</Label>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="bg-gray-800 border-gray-600 mt-1"
                />
              </div>
            </div>
          )}

          {/* Duration preview */}
          {calcHours() && (
            <div className="bg-blue-950/30 border border-blue-700/40 rounded-lg px-3 py-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-400" />
              <span className="text-sm text-blue-300 font-medium">{calcHours()}</span>
              <span className="text-xs text-blue-400/60">requested</span>
            </div>
          )}

          {/* Dates the office has blocked for leave */}
          {stoppingBlocks.length > 0 ? (
            <div className="rounded-lg border border-red-700/60 bg-red-950/40 px-3 py-2 space-y-1.5">
              <div className="flex items-center gap-2 text-sm font-semibold text-red-300">
                <Ban className="h-4 w-4" /> You can't request leave for these dates
              </div>
              {stoppingBlocks.map((b) => (
                <div key={b.id} className="text-sm text-red-200">
                  <span className="text-xs text-red-300/80">{b.name} ({formatBlockDates(b)})</span>
                  <div>{b.message}</div>
                </div>
              ))}
            </div>
          ) : (
            upcomingBlocks.length > 0 && (
              <div className="rounded-lg border border-gray-700 bg-gray-800/50 px-3 py-2 text-xs text-gray-300 space-y-0.5">
                <div className="font-semibold text-gray-200 flex items-center gap-1.5">
                  <Ban className="h-3.5 w-3.5 text-red-400" /> Leave can't be requested for:
                </div>
                {upcomingBlocks.map((b) => (
                  <div key={b.id}>
                    {b.name} — {formatBlockDates(b)}
                  </div>
                ))}
              </div>
            )
          )}

          {/* Reason */}
          <div>
            <Label className="text-gray-300 text-xs uppercase tracking-wider">Reason</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Medical appointment, family event, personal day..."
              className="bg-gray-800 border-gray-600 mt-1 min-h-[80px]"
            />
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 bg-amber-950/30 border border-amber-700/30 rounded-lg px-3 py-2">
            <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-300">
              Your request will be submitted as <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-400 px-1 py-0 mx-0.5">Pending</Badge> and requires admin approval.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || stoppingBlocks.length > 0} className="bg-blue-600 hover:bg-blue-700">
            {saving ? (
              <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Submitting...</>
            ) : (
              "Submit Request"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TimeOffRequestDialog;
